import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from 'cn';
import { Slot } from 'radix-ui';

const badgeVariants = cva(
  'tw:inline-flex tw:w-fit tw:shrink-0 tw:items-center tw:justify-center tw:gap-1 tw:overflow-hidden tw:rounded-full tw:px-1.25 tw:py-0.75 tw:text-2xs tw:font-semibold tw:whitespace-nowrap tw:transition-[color,box-shadow] tw:focus-visible:border-ring tw:focus-visible:ring-[3px] tw:focus-visible:ring-ring/50 tw:aria-invalid:border-destructive tw:aria-invalid:ring-destructive/20 tw:dark:aria-invalid:ring-destructive/40 tw:[&>svg]:pointer-events-none tw:[&>svg]:size-3',
  {
    variants: {
      variant: {
        default:
          'tw:bg-primary tw:text-primary-foreground tw:[a&]:hover:bg-primary/90',
        secondary:
          'tw:bg-secondary tw:text-secondary-foreground tw:[a&]:hover:bg-secondary/90',
        destructive:
          'tw:bg-destructive tw:text-white tw:focus-visible:ring-destructive/20 tw:dark:bg-destructive/60 tw:dark:focus-visible:ring-destructive/40 tw:[a&]:hover:bg-destructive/90',
        outline:
          'tw:border tw:border-border tw:text-foreground tw:[a&]:hover:bg-accent tw:[a&]:hover:text-accent-foreground',
        ghost: 'tw:[a&]:hover:bg-accent tw:[a&]:hover:text-accent-foreground',
        link: 'tw:text-primary tw:underline-offset-4 tw:[a&]:hover:underline',
        // Variantes da Actio (não existem no shadcn).
        success:
          'tw:bg-success tw:text-success-foreground tw:focus-visible:ring-success/20 tw:[a&]:hover:bg-success/90',
        warning:
          'tw:bg-warning tw:text-warning-foreground tw:focus-visible:ring-warning/20 tw:[a&]:hover:bg-warning/90',
        info: 'tw:bg-info tw:text-info-foreground tw:focus-visible:ring-info/20 tw:[a&]:hover:bg-info/90',
        defaultLight: 'tw:bg-primary/15 tw:text-primary',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const Badge = React.forwardRef<
  HTMLSpanElement,
  React.ComponentPropsWithoutRef<'span'> &
    VariantProps<typeof badgeVariants> & { asChild?: boolean }
>(function Badge(
  { className, variant = 'default', asChild = false, ...props },
  ref
) {
  const Comp = asChild ? Slot.Root : 'span';

  return (
    <Comp
      ref={ref}
      data-slot="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  );
});

export { Badge, badgeVariants };
